import React from "react";
import { Clock3, ArrowRightLeft, TrendingUp } from "lucide-react";

const formatHours = (hours = 0) => {
  if (hours < 24) {
    return `${hours} hrs`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return `${days}d ${remainingHours}h`;
};

export default function StageDurationAnalytics({ data = [] }) {
  const totalMoves = data.reduce((sum, item) => sum + item.totalMoves, 0);

  const avgPipelineDuration =
    data.length > 0
      ? Math.round(
          data.reduce((sum, item) => sum + item.avgHours, 0) / data.length,
        )
      : 0;

  return (
    <div className="space-y-5">
      {/* Header */}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Stage Duration Analytics
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Average time spent before moving to the next stage
          </p>
        </div>
      </div>

      {/* Top Cards */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Stage Moves</p>

              <h3 className="text-3xl font-bold mt-2">{totalMoves}</h3>
            </div>

            <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
              <ArrowRightLeft size={22} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Pipeline Duration</p>

              <h3 className="text-3xl font-bold mt-2">
                {formatHours(avgPipelineDuration)}
              </h3>
            </div>

            <div className="h-12 w-12 rounded-xl bg-green-50 flex items-center justify-center">
              <Clock3 size={22} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Stages Tracked</p>

              <h3 className="text-3xl font-bold mt-2">{data.length}</h3>
            </div>

            <div className="h-12 w-12 rounded-xl bg-purple-50 flex items-center justify-center">
              <TrendingUp size={22} className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50">
          <h3 className="font-semibold">Stage Duration Breakdown</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  Stage
                </th>

                <th className="px-4 py-3 text-center text-sm font-semibold">
                  Moves
                </th>

                <th className="px-4 py-3 text-center text-sm font-semibold">
                  Avg Duration
                </th>

                <th className="px-4 py-3 text-center text-sm font-semibold">
                  Min Duration
                </th>

                <th className="px-4 py-3 text-center text-sm font-semibold">
                  Max Duration
                </th>

                <th className="px-4 py-3 text-center text-sm font-semibold">
                  Probability
                </th>
              </tr>
            </thead>

            <tbody>
              {data.map((item) => (
                <tr key={item._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: item.stageColor,
                        }}
                      />

                      <div>
                        <p className="font-medium">{item.stageName}</p>

                        <p className="text-xs text-gray-500">
                          {item.stageCode}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center font-medium">
                    {item.totalMoves}
                  </td>

                  <td className="px-4 py-4 text-center">
                    {formatHours(item.avgHours)}
                  </td>

                  <td className="px-4 py-4 text-center">
                    {formatHours(item.minHours)}
                  </td>

                  <td className="px-4 py-4 text-center">
                    {formatHours(item.maxHours)}
                  </td>

                  <td className="px-4 py-4 text-center">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={{
                        backgroundColor: item.stageColor,
                      }}
                    >
                      {item.probability}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
